import { Typography, type TypographyProps, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useIsDarkMode } from "web_myplann/Shared/Renderer/useIsDarkMode";
import { signedNumberFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { signedPercentageValueFormatter } from "web_myplann/Shared/Utils/percentageFormatters";
import {
	DANGER_MYPLANN_DARK,
	DANGER_MYPLANN_LIGHT,
	SUCCESS_MYPLANN_DARK,
	SUCCESS_MYPLANN_LIGHT,
} from "web_myplann/Utils/constants";

type Props = {
	textBefore?: string;
	change?: number | null;
	textAfter?: string;
	variant?: TypographyProps["variant"];
	fontSize?: number;
	fontWeight?: number;
	lineHeight?: number | string;
	color?: string;
};

export const PercentageDelta: FC<Props> = ({
	textBefore,
	change,
	textAfter,
	variant,
	fontSize,
	fontWeight,
	lineHeight,
	color,
}) => {
	const theme = useTheme();
	const isDark = useIsDarkMode();

	const computedColor = useMemo(() => {
		if (change == null || change === 0) {
			return theme.palette.deprecated.grey[500];
		}

		const isPositive = change > 0;

		if (isPositive) {
			return isDark ? SUCCESS_MYPLANN_DARK : SUCCESS_MYPLANN_LIGHT;
		}

		return isDark ? DANGER_MYPLANN_DARK : DANGER_MYPLANN_LIGHT;
	}, [change, isDark, theme.palette.deprecated.grey]);

	if (change == null || Number.isNaN(change)) {
		return null;
	}

	const formattedChange =
		change === 0
			? signedNumberFormatter.format(change)
			: signedPercentageValueFormatter.format(change);

	return (
		<Typography
			variant={variant}
			fontSize={fontSize ?? (variant ? undefined : 14)}
			fontWeight={fontWeight ?? (variant ? undefined : 600)}
			lineHeight={lineHeight}
			color={color ?? computedColor}
		>
			{textBefore}
			{formattedChange}
			{"\u00A0%"}
			{textAfter}
		</Typography>
	);
};
